import React from "react";
import {Box, Drawer, IconButton, Skeleton, Typography} from "@mui/material";
import {Close} from "@mui/icons-material";
import {isAxiosError} from "axios";
import {REPORT_CSS} from "../contractCoverageOverview";
import {useSupplierOverviewQuery} from "../useSupplierOverviewQuery";

type Props = {
    open: boolean;
    onClose: () => void;
    parentId: string | null;
};

// Render the report section verbatim inside an isolated iframe: the source
// report's exact CSS + exact HTML, so the overview is pixel-identical to what
// the report author produced and its styles never leak into (or inherit from)
// the app.
function buildSrcDoc(bodyHtml: string): string {
    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">`
        + `<meta name="viewport" content="width=device-width, initial-scale=1.0">`
        + `<style>${REPORT_CSS}</style></head>`
        + `<body><div class="page">${bodyHtml}</div></body></html>`;
}

export const OverviewDrawer: React.FC<Props> = ({open, onClose, parentId}) => {
    const {data: overview, isPending, isError, error} = useSupplierOverviewQuery(open ? parentId : null);
    const isNotFound = isError && isAxiosError(error) && error.response?.status === 404;

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{className: 'cci-overview-paper'}}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            <div className="cci-overview">
                <header className="cci-overview-bar">
                    <span className="cci-overview-eyebrow">Contract analysis overview</span>
                    <IconButton onClick={onClose} size="small"><Close/></IconButton>
                </header>

                {isPending && (
                    <Box className="cci-overview-state cci-overview-state--loading" aria-busy="true" aria-label="Loading overview">
                        <Skeleton variant="text" width="40%" height={28} animation="wave"/>
                        <Skeleton variant="rounded" height={72} animation="wave" sx={{mt: 2}}/>
                        <Skeleton variant="rounded" height={160} animation="wave" sx={{mt: 2}}/>
                        <Skeleton variant="rounded" height={120} animation="wave" sx={{mt: 2}}/>
                        <Skeleton variant="rounded" height={200} animation="wave" sx={{mt: 2}}/>
                    </Box>
                )}

                {!isPending && isNotFound && (
                    <Box className="cci-overview-state cci-overview-state--empty">
                        <Typography variant="body1" color="text.secondary">
                            There is no report for this supplier
                        </Typography>
                    </Box>
                )}

                {!isPending && isError && !isNotFound && (
                    <Box className="cci-overview-state cci-overview-state--empty">
                        <Typography variant="body1" color="text.secondary">
                            Could not load the report for this supplier
                        </Typography>
                    </Box>
                )}

                {!isPending && overview && (
                    <iframe
                        className="cci-overview-frame"
                        title={`Contract analysis overview — ${overview.title}`}
                        srcDoc={buildSrcDoc(overview.html)}
                    />
                )}
            </div>
        </Drawer>
    );
};
